import React, { useRef, useState } from 'react';
import {
  Box, Typography, IconButton, LinearProgress, Dialog,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VideocamIcon from '@mui/icons-material/Videocam';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { attachmentService } from '../../store/attachment.service';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const isImage = (type) => type && type.startsWith('image/');
const isVideo = (type) => type && type.startsWith('video/');

export const FileUpload = ({ attachments = [], onChange, maxFiles = 10 }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // { url, type, name } for lightbox

  const handleFiles = async (fileList) => {
    setError('');
    const files = Array.from(fileList);
    const remaining = maxFiles - attachments.length;
    if (remaining <= 0) {
      setError(`Max ${maxFiles} files allowed`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    const oversized = toUpload.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length) {
      setError(`${oversized[0].name} exceeds 50MB limit`);
      return;
    }

    setUploading(true);
    const newAttachments = [...attachments];
    for (const file of toUpload) {
      try {
        const res = await attachmentService.upload(file);
        newAttachments.push(res.data);
      } catch (err) {
        setError(`Failed to upload ${file.name}`);
      }
    }
    onChange(newAttachments);
    setUploading(false);
  };

  const handleRemove = async (idx) => {
    const att = attachments[idx];
    try {
      await attachmentService.delete(att.key);
    } catch (e) {
      // File may already be gone — remove from list anyway
    }
    onChange(attachments.filter((_, i) => i !== idx));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  };

  const openPreview = (att) => {
    if (isImage(att.type) || isVideo(att.type)) {
      setPreview(att);
    } else {
      window.open(att.url, '_blank');
    }
  };

  const renderThumbnail = (att) => {
    if (isImage(att.type)) {
      return (
        <Box
          component="img"
          src={att.url}
          alt={att.name}
          sx={{ width: 36, height: 36, borderRadius: 0.5, objectFit: 'cover', flexShrink: 0, cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); openPreview(att); }}
        />
      );
    }
    if (isVideo(att.type)) {
      return (
        <Box
          onClick={(e) => { e.stopPropagation(); openPreview(att); }}
          sx={{
            width: 36, height: 36, borderRadius: 0.5,
            backgroundColor: 'action.selected',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, cursor: 'pointer', position: 'relative',
          }}
        >
          <VideocamIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          <PlayArrowIcon sx={{ fontSize: 12, color: 'primary.main', position: 'absolute', bottom: 1, right: 1 }} />
        </Box>
      );
    }
    return (
      <InsertDriveFileIcon
        sx={{ fontSize: 22, color: 'text.secondary', flexShrink: 0, cursor: 'pointer' }}
        onClick={(e) => { e.stopPropagation(); openPreview(att); }}
      />
    );
  };

  return (
    <Box>
      {/* Drop zone */}
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: dragOver ? 'action.hover' : 'transparent',
          transition: 'all 0.2s',
          '&:hover': { borderColor: 'primary.light', backgroundColor: 'action.hover' },
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12 }}>
          Drop files here or click to upload
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
          Images, videos, documents — max 50MB per file
        </Typography>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => { if (e.target.files.length) handleFiles(e.target.files); e.target.value = ''; }}
        />
      </Box>

      {uploading && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
      {error && (
        <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}

      {/* Attached files list */}
      {attachments.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {attachments.map((att, idx) => (
            <Box
              key={att.key || idx}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                p: 0.75, borderRadius: 1, backgroundColor: 'action.hover',
              }}
            >
              {renderThumbnail(att)}
              <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => openPreview(att)}>
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {att.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10 }}>
                  {isVideo(att.type) ? 'VIDEO' : (att.type?.split('/')[1]?.toUpperCase() || 'FILE')}
                </Typography>
              </Box>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); window.open(att.url, '_blank'); }}
                sx={{ width: 20, height: 20 }}>
                <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              </IconButton>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemove(idx); }}
                sx={{ width: 20, height: 20 }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Preview dialog for images and videos */}
      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden', backgroundColor: '#000' } }}
      >
        {preview && (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setPreview(null)}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
            >
              <CloseIcon />
            </IconButton>
            {isImage(preview.type) && (
              <Box component="img" src={preview.url} alt={preview.name}
                sx={{ display: 'block', maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain' }} />
            )}
            {isVideo(preview.type) && (
              <Box
                component="video"
                src={preview.url}
                controls
                autoPlay
                sx={{ display: 'block', maxWidth: '80vw', maxHeight: '80vh', outline: 'none' }}
              />
            )}
          </Box>
        )}
      </Dialog>
    </Box>
  );
};
